import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  MessageSquare, User, Search, Loader2, Users, CheckSquare, Square,
  UserPlus, UserMinus, ChevronDown, ChevronLeft, ChevronRight, Check, X, Filter,
} from "lucide-react";
import { useLiveChat } from "../../contexts/LiveChatContext";
import { useTimezone } from "../../contexts/TimezoneContext";
import { formatTimestampForDisplay } from "../../utils/timestampUtils";
import { getLivechatAccessInfo, getAvailableAgents, assignChat } from "../../apis/livechat";
import { toast } from "react-toastify";

/**
 * ChatList — sidebar conversation list with tabs, filters, multi-select, pagination.
 * Squared WhatsApp-style theme. Consistent with LiveChats / ChatHeader / MessageInput.
 */
const ChatList = ({ searchQuery, setSearchQuery, onChatSelect, onChatsAssigned }) => {
  const getWindowStateForTab = (tab) => (tab === "active" ? "active" : "non-active");
  const clampPreview = (value, maxChars = 44) => {
    const s = String(value ?? "").replace(/\s+/g, " ").trim();
    if (!s) return "";
    return s.length > maxChars ? `${s.slice(0, maxChars)}…` : s;
  };

  const {
    chats, selectedChat, loading, chatsTotal, chatsPageSize,
    chatsWindowCounts, chatStatusesList, filters, setFilters,
    fetchConversations, accessLevel, selectedPhoneId,
  } = useLiveChat();

  // Subscribing here ensures the sidebar re-renders when the user changes the
  // account timezone, so all "X ago" / dates update immediately.
  const { timezone: activeTimezone } = useTimezone();

  const [activeSubTab, setActiveSubTab] = useState("active");
  const [tabPages, setTabPages] = useState({ active: 1, "non-active": 1 });

  // Multi-select
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState(new Set());
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [assigning, setAssigning] = useState(false);

  // Filters
  const defaultFilters = { agent_ids: [], status_ids: [], date_from: "", date_to: "" };
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [filterDraft, setFilterDraft] = useState(defaultFilters);

  const { role: userRole } = getLivechatAccessInfo();
  const isSuperAdmin = userRole === "super_admin";
  const hasAllChatsAccess = accessLevel === "all";

  const filteredChats = useMemo(() =>
    chats.filter((chat) =>
      chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.phone.includes(searchQuery)
    ), [chats, searchQuery]);

  const activeTotal = chatsWindowCounts?.active ?? 0;
  const nonActiveTotal = chatsWindowCounts?.non_active ?? 0;
  const currentTabTotal = activeSubTab === "active" ? activeTotal : nonActiveTotal;
  const currentTabPage = tabPages[activeSubTab] || 1;
  const totalPages = Math.max(1, Math.ceil(currentTabTotal / chatsPageSize));

  const hasActiveFilters =
    filters.agent_ids.length > 0 || filters.status_ids.length > 0 || filters.date_from || filters.date_to;

  useEffect(() => {
    const page = tabPages[activeSubTab] || 1;
    fetchConversations(page, filters, { windowState: getWindowStateForTab(activeSubTab) });
  }, [activeSubTab, selectedPhoneId]); // eslint-disable-line

  useEffect(() => {
    // When switching WhatsApp number, always reset pagination to page 1 and refetch.
    setTabPages({ active: 1, "non-active": 1 });
    fetchConversations(1, filters, { windowState: getWindowStateForTab(activeSubTab) });
  }, [selectedPhoneId]);

  useEffect(() => {
    if (showAssignDropdown && isSuperAdmin && selectedChatIds.size > 0) {
      setLoadingAgents(true);
      getAvailableAgents()
        .then((res) => { if (res.data.success) setAvailableAgents(res.data.data || []); })
        .catch(() => toast.error("Failed to load agents"))
        .finally(() => setLoadingAgents(false));
    }
  }, [showAssignDropdown, isSuperAdmin, selectedChatIds.size]);

  const openFilterPopup = () => {
    setFilterDraft({ ...filters });
    setShowFilterPopup(true);
    if (hasAllChatsAccess) {
      setLoadingAgents(true);
      getAvailableAgents()
        .then((res) => { if (res.data?.success) setAvailableAgents(res.data.data || []); })
        .catch(() => toast.error("Failed to load agents"))
        .finally(() => setLoadingAgents(false));
    }
  };

  const applyFilterPopup = () => {
    setFilters(filterDraft);
    setTabPages((prev) => ({ ...prev, [activeSubTab]: 1 }));
    fetchConversations(1, filterDraft, { windowState: getWindowStateForTab(activeSubTab) });
    setShowFilterPopup(false);
  };

  const clearFilters = () => {
    setFilterDraft(defaultFilters);
    setFilters(defaultFilters);
    setTabPages((prev) => ({ ...prev, [activeSubTab]: 1 }));
    fetchConversations(1, defaultFilters, { windowState: getWindowStateForTab(activeSubTab) });
    setShowFilterPopup(false);
  };

  const toggleDraftAgent = (id) =>
    setFilterDraft((d) => ({
      ...d,
      agent_ids: d.agent_ids.includes(id) ? d.agent_ids.filter((x) => x !== id) : [...d.agent_ids, id],
    }));

  const toggleDraftStatus = (id) =>
    setFilterDraft((d) => ({
      ...d,
      status_ids: d.status_ids.includes(id) ? d.status_ids.filter((x) => x !== id) : [...d.status_ids, id],
    }));

  const toggleMultiSelectMode = () => {
    setMultiSelectMode((prev) => !prev);
    if (multiSelectMode) setSelectedChatIds(new Set());
    setShowAssignDropdown(false);
  };

  const toggleChatSelection = (e, chatId) => {
    e.stopPropagation();
    setSelectedChatIds((prev) => {
      const next = new Set(prev);
      next.has(chatId) ? next.delete(chatId) : next.add(chatId);
      return next;
    });
  };

  const refetchCurrentTab = () =>
    fetchConversations(currentTabPage, filters, { windowState: getWindowStateForTab(activeSubTab) });

  const handleBulkAssign = async (agentId) => {
    if (!agentId || selectedChatIds.size === 0 || assigning) return;
    const agentName = availableAgents.find((a) => a.id === agentId)?.name || "Agent";
    setAssigning(true);
    setShowAssignDropdown(false);
    const selected = filteredChats.filter((c) => selectedChatIds.has(c.id));
    let success = 0, failed = 0;
    for (const chat of selected) {
      try {
        const res = await assignChat(chat.customer_phone, chat.phone_number_id, agentId);
        res.data?.success ? success++ : failed++;
      } catch { failed++; }
    }
    setAssigning(false);
    setSelectedChatIds(new Set());
    setMultiSelectMode(false);
    if (success) { toast.success(`${success} chat(s) assigned to ${agentName}`); refetchCurrentTab(); onChatsAssigned?.(); }
    if (failed) toast.error(`${failed} chat(s) failed to assign`);
  };

  const handleBulkUnassign = async () => {
    if (selectedChatIds.size === 0 || assigning) return;
    setAssigning(true);
    const selected = filteredChats.filter((c) => selectedChatIds.has(c.id));
    let success = 0, failed = 0;
    for (const chat of selected) {
      try {
        const res = await assignChat(chat.customer_phone, chat.phone_number_id, null);
        res.data?.success ? success++ : failed++;
      } catch { failed++; }
    }
    setAssigning(false);
    setSelectedChatIds(new Set());
    setMultiSelectMode(false);
    if (success) { toast.success(`${success} chat(s) unassigned`); refetchCurrentTab(); onChatsAssigned?.(); }
    if (failed) toast.error(`${failed} chat(s) failed to unassign`);
  };

  /* ─── Avatar helper ─── */
  const getInitials = (name = "") => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const AVATAR_COLORS = ["#25D366","#128C7E","#3b82f6","#8b5cf6","#f59e0b","#ec4899","#14b8a6","#6366f1"];
  const getAvatarColor = (str = "") => AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];

  return (
    <div className="cl-root">
      {/* ── Sub-tabs ── */}
      <div className="cl-tabs">
        {[
          { key: "active", label: "Active", count: activeTotal },
          { key: "non-active", label: "Non-Active", count: nonActiveTotal },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSubTab(tab.key)}
            className={`cl-tab${activeSubTab === tab.key ? " cl-tab--active" : ""}`}
          >
            {tab.label}
            <span className="cl-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* ── Search + multi-select toggle ── */}
      <div className="cl-search-row">
        <div className="cl-search-wrap">
          <Search size={14} className="cl-search-icon" />
          <input
            type="text"
            placeholder="Search conversations…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cl-search-input"
            aria-label="Search conversations"
          />
          {searchQuery && (
            <button className="cl-search-clear" onClick={() => setSearchQuery("")} aria-label="Clear search">
              <X size={13} />
            </button>
          )}
        </div>
        {isSuperAdmin && (
          <button
            onClick={toggleMultiSelectMode}
            className={`cl-icon-btn${multiSelectMode ? " cl-icon-btn--active" : ""}`}
            title={multiSelectMode ? "Cancel selection" : "Multi-select"}
          >
            {multiSelectMode ? <X size={14} /> : <CheckSquare size={14} />}
          </button>
        )}
        <button
          onClick={openFilterPopup}
          className={`cl-icon-btn${hasActiveFilters ? " cl-icon-btn--filtered" : ""}`}
          title="Filters"
        >
          <Filter size={14} />
          {hasActiveFilters && (
            <span className="cl-filter-dot" />
          )}
        </button>
      </div>

      {/* ── Bulk action bar ── */}
      {isSuperAdmin && multiSelectMode && selectedChatIds.size > 0 && (
        <div className="cl-bulk-bar">
          <span className="cl-bulk-count">{selectedChatIds.size} selected</span>
          <div className="cl-bulk-actions">
            <button
              onClick={handleBulkUnassign}
              disabled={assigning}
              className="cl-bulk-btn cl-bulk-btn--unassign"
            >
              {assigning ? <Loader2 size={12} className="cl-spin" /> : <UserMinus size={12} />}
              Unassign
            </button>
            <div className="cl-bulk-assign-wrap">
              <button
                onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                disabled={assigning}
                className="cl-bulk-btn cl-bulk-btn--assign"
              >
                {assigning ? <Loader2 size={12} className="cl-spin" /> : <UserPlus size={12} />}
                Assign
                <ChevronDown size={10} />
              </button>
              {showAssignDropdown && (
                <>
                  <div className="cl-overlay" onClick={() => setShowAssignDropdown(false)} />
                  <div className="cl-dropdown cl-dropdown--right">
                    <div className="cl-dropdown-header">Assign {selectedChatIds.size} chat(s) to</div>
                    {loadingAgents ? (
                      <div className="cl-dropdown-loader"><Loader2 size={16} className="cl-spin" /></div>
                    ) : availableAgents.length === 0 ? (
                      <div className="cl-dropdown-empty">No agents available</div>
                    ) : (
                      <div className="cl-dropdown-list">
                        {availableAgents.map((agent) => (
                          <button key={agent.id} onClick={() => handleBulkAssign(agent.id)} disabled={assigning} className="cl-agent-row">
                            <div className="cl-agent-avatar" style={{ background: getAvatarColor(agent.name) }}>
                              {getInitials(agent.name)}
                            </div>
                            <div className="cl-agent-info">
                              <span className="cl-agent-name">{agent.name}</span>
                              <span className="cl-agent-email">{agent.email}</span>
                            </div>
                            <Check size={13} className="cl-agent-check" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Chat list ── */}
      <div className="cl-list">
        {loading ? (
          <div className="cl-state-center">
            <Loader2 size={28} className="cl-spin cl-spin--green" />
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="cl-state-center">
            <MessageSquare size={36} className="cl-empty-icon" strokeWidth={1.2} />
            <p className="cl-empty-label">
              {searchQuery ? "No conversations found" : activeSubTab === "active" ? "No active chats" : "No non-active chats"}
            </p>
          </div>
        ) : (
          <>
            {filteredChats.map((chat) => {
              const isSelected = selectedChat?.id === chat.id && !multiSelectMode;
              const isChecked = selectedChatIds.has(chat.id);
              return (
                <motion.div
                  key={chat.id}
                  onClick={() => {
                    if (multiSelectMode && isSuperAdmin) {
                      setSelectedChatIds((prev) => {
                        const next = new Set(prev);
                        next.has(chat.id) ? next.delete(chat.id) : next.add(chat.id);
                        return next;
                      });
                    } else {
                      onChatSelect(chat);
                    }
                  }}
                  className={`cl-chat-item${isSelected ? " cl-chat-item--active" : ""}${isChecked ? " cl-chat-item--checked" : ""}`}
                  whileTap={{ scale: 0.995 }}
                >
                  {/* Checkbox */}
                  {isSuperAdmin && multiSelectMode && (
                    <div
                      className="cl-checkbox"
                      onClick={(e) => toggleChatSelection(e, chat.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleChatSelection(e, chat.id); } }}
                      aria-label={isChecked ? "Deselect" : "Select"}
                    >
                      {isChecked
                        ? <CheckSquare size={16} className="cl-checkbox-on" />
                        : <Square size={16} className="cl-checkbox-off" />}
                    </div>
                  )}

                  {/* Avatar */}
                  <div className="cl-avatar-wrap">
                    <div
                      className="cl-avatar"
                      style={{ background: getAvatarColor(chat.name) }}
                      aria-hidden="true"
                    >
                      {getInitials(chat.name)}
                    </div>
                    {!chat.is_window_open && <span className="cl-window-dot" title="Window closed" />}
                  </div>

                  {/* Info */}
                  <div className="cl-chat-info">
                    <div className="cl-chat-top">
                      <span className="cl-chat-name">{chat.name}</span>
                      <span className="cl-chat-time">
                        {formatTimestampForDisplay(
                          chat.last_message_at || chat.last_message?.sent_at,
                          activeTimezone
                        )}
                      </span>
                    </div>
                    <div className="cl-chat-mid">
                      <span className="cl-chat-preview">{clampPreview(chat.lastMessage)}</span>
                      {chat.unread > 0 && <span className="cl-badge">{chat.unread}</span>}
                    </div>
                    <div className="cl-chat-meta">
                      {isSuperAdmin && chat.assigned_agent_name ? (
                        <span className="cl-meta-chip cl-meta-chip--agent">
                          <Users size={9} /> {chat.assigned_agent_name}
                        </span>
                      ) : isSuperAdmin && !chat.assigned_agent_id ? (
                        <span className="cl-meta-chip cl-meta-chip--unassigned">Unassigned</span>
                      ) : null}
                      <span
                        className="cl-status-pill"
                        style={{
                          background: (chat.status_color || "#9ca3af") + "22",
                          color: chat.status_color || "#6b7280",
                        }}
                      >
                        {chat.status_name || "No status"}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Pagination */}
            {currentTabTotal > chatsPageSize && (
              <div className="cl-pagination">
                <span className="cl-page-info">
                  Page {currentTabPage} of {totalPages} <span className="cl-page-total">({currentTabTotal})</span>
                </span>
                <div className="cl-page-btns">
                  <button
                    onClick={() => {
                      const p = Math.max(1, currentTabPage - 1);
                      setTabPages((prev) => ({ ...prev, [activeSubTab]: p }));
                      fetchConversations(p, filters, { windowState: getWindowStateForTab(activeSubTab) });
                    }}
                    disabled={currentTabPage <= 1 || loading}
                    className="cl-page-btn"
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <button
                    onClick={() => {
                      const p = Math.min(totalPages, currentTabPage + 1);
                      setTabPages((prev) => ({ ...prev, [activeSubTab]: p }));
                      fetchConversations(p, filters, { windowState: getWindowStateForTab(activeSubTab) });
                    }}
                    disabled={currentTabPage >= totalPages || loading}
                    className="cl-page-btn"
                    aria-label="Next page"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Filter popup ── */}
      {showFilterPopup && (
        <>
          <div className="cl-modal-backdrop" onClick={() => setShowFilterPopup(false)} />
          <div className="cl-modal" role="dialog" aria-label="Filter conversations">
            <div className="cl-modal-header">
              <span className="cl-modal-title">Filters</span>
              <button className="cl-modal-close" onClick={() => setShowFilterPopup(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="cl-modal-body">
              {hasAllChatsAccess && (
                <div className="cl-filter-section">
                  <p className="cl-filter-label">Agents</p>
                  <div className="cl-filter-list">
                    {loadingAgents ? (
                      <div className="cl-dropdown-loader"><Loader2 size={16} className="cl-spin" /></div>
                    ) : availableAgents.map((agent) => (
                      <button key={agent.id} onClick={() => toggleDraftAgent(agent.id)} className="cl-filter-row">
                        {filterDraft.agent_ids.includes(agent.id)
                          ? <CheckSquare size={14} className="cl-checkbox-on" />
                          : <Square size={14} className="cl-checkbox-off" />}
                        <span className="cl-filter-row-label">{agent.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="cl-filter-section">
                <p className="cl-filter-label">Status</p>
                <div className="cl-filter-list">
                  {chatStatusesList.map((s) => (
                    <button key={s.id} onClick={() => toggleDraftStatus(s.id)} className="cl-filter-row">
                      {filterDraft.status_ids.includes(s.id)
                        ? <CheckSquare size={14} className="cl-checkbox-on" />
                        : <Square size={14} className="cl-checkbox-off" />}
                      <span className="cl-status-dot-sm" style={{ background: s.color || "#9ca3af" }} />
                      <span className="cl-filter-row-label">{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="cl-filter-dates">
                <div className="cl-filter-date">
                  <label className="cl-filter-label">From</label>
                  <input
                    type="datetime-local"
                    value={filterDraft.date_from ? (filterDraft.date_from.includes("T") ? filterDraft.date_from.slice(0, 16) : `${filterDraft.date_from}T00:00`) : ""}
                    onChange={(e) => setFilterDraft((d) => ({ ...d, date_from: e.target.value || "" }))}
                    className="cl-date-input"
                  />
                </div>
                <div className="cl-filter-date">
                  <label className="cl-filter-label">To</label>
                  <input
                    type="datetime-local"
                    value={filterDraft.date_to ? (filterDraft.date_to.includes("T") ? filterDraft.date_to.slice(0, 16) : `${filterDraft.date_to}T23:59`) : ""}
                    onChange={(e) => setFilterDraft((d) => ({ ...d, date_to: e.target.value || "" }))}
                    className="cl-date-input"
                  />
                </div>
              </div>
            </div>
            <div className="cl-modal-footer">
              <button onClick={clearFilters} className="cl-modal-btn cl-modal-btn--ghost">Clear</button>
              <button onClick={applyFilterPopup} className="cl-modal-btn cl-modal-btn--primary">Apply</button>
            </div>
          </div>
        </>
      )}

      <style>{`
        .cl-root {
          --cl-green: #25D366;
          --cl-green-dk: #128C7E;
          --cl-border: rgba(0,0,0,0.09);
          --cl-text: #111b21;
          --cl-text-2: #54656f;
          --cl-text-3: #8696a0;
          --cl-surface: #f0f2f5;
          --cl-hover: rgba(0,0,0,0.03);
          --cl-active: rgba(37,211,102,0.07);
          --cl-radius: 4px;
          --cl-pad: 12px;
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }

        /* Tabs */
        .cl-tabs { display: flex; border-bottom: 0.5px solid var(--cl-border); background: #fff; flex-shrink: 0; padding: 0 var(--cl-pad); }
        .cl-tab {
          flex: 1; padding: 10px 6px; font-size: 12.5px; font-weight: 500;
          color: var(--cl-text-3); background: none; border: none; border-bottom: 2px solid transparent;
          cursor: pointer; transition: all 0.15s; display: flex; align-items: center; justify-content: center; gap: 5px;
        }
        .cl-tab:hover { color: var(--cl-text-2); background: var(--cl-hover); }
        .cl-tab--active { color: var(--cl-green-dk); border-bottom-color: var(--cl-green); }
        .cl-tab-count {
          font-size: 10px; font-weight: 600; padding: 1px 5px; border-radius: 10px;
          background: var(--cl-surface); color: var(--cl-text-3);
        }
        .cl-tab--active .cl-tab-count { background: rgba(37,211,102,0.12); color: var(--cl-green-dk); }

        /* Search row */
        .cl-search-row { display: flex; align-items: center; gap: 8px; padding: 12px var(--cl-pad) 10px; border-bottom: 0.5px solid var(--cl-border); flex-shrink: 0; }
        .cl-search-wrap { flex: 1; display: flex; align-items: center; gap: 8px; background: var(--cl-surface); border-radius: var(--cl-radius); padding: 8px 12px; min-width: 0; }
        .cl-search-icon { color: var(--cl-text-3); flex-shrink: 0; }
        .cl-search-input { flex: 1; border: none; background: transparent; outline: none; font-size: 13px; color: var(--cl-text); min-width: 0; }
        .cl-search-input::placeholder { color: var(--cl-text-3); }
        .cl-search-clear { background: none; border: none; cursor: pointer; color: var(--cl-text-3); display: flex; padding: 0; }
        .cl-icon-btn {
          width: 32px; height: 32px; flex-shrink: 0; border-radius: var(--cl-radius);
          border: 0.5px solid var(--cl-border); background: #fff; color: var(--cl-text-2);
          display: flex; align-items: center; justify-content: center; cursor: pointer;
          transition: all 0.15s; position: relative;
        }
        .cl-icon-btn:hover { background: var(--cl-surface); }
        .cl-icon-btn--active { background: var(--cl-green); color: #fff; border-color: var(--cl-green); }
        .cl-icon-btn--filtered { border-color: var(--cl-green); color: var(--cl-green); }
        .cl-filter-dot {
          width: 6px; height: 6px; border-radius: 50%; background: var(--cl-green);
          position: absolute; top: 4px; right: 4px;
        }

        /* Bulk bar */
        .cl-bulk-bar {
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
          padding: 8px var(--cl-pad); background: rgba(37,211,102,0.06); border-bottom: 0.5px solid rgba(37,211,102,0.2); flex-shrink: 0;
        }
        .cl-bulk-count { font-size: 12px; font-weight: 600; color: var(--cl-green-dk); }
        .cl-bulk-actions { display: flex; gap: 6px; }
        .cl-bulk-btn {
          display: flex; align-items: center; gap: 5px; padding: 5px 10px;
          border-radius: var(--cl-radius); border: none; cursor: pointer; font-size: 11.5px; font-weight: 600;
          transition: opacity 0.15s;
        }
        .cl-bulk-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .cl-bulk-btn--unassign { background: #4b5563; color: #fff; }
        .cl-bulk-btn--assign { background: var(--cl-green); color: #fff; }
        .cl-bulk-assign-wrap { position: relative; }

        /* Chat list */
        .cl-list { flex: 1; overflow-y: auto; padding: 6px var(--cl-pad); scrollbar-width: thin; scrollbar-color: #ddd transparent; }
        .cl-list::-webkit-scrollbar { width: 3px; }
        .cl-list::-webkit-scrollbar-thumb { background: #ddd; border-radius: 3px; }

        .cl-state-center { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; gap: 10px; }
        .cl-empty-icon { color: #ccc; }
        .cl-empty-label { font-size: 13px; color: var(--cl-text-3); }
        .cl-spin { animation: cl-rotate 0.7s linear infinite; }
        @keyframes cl-rotate { to { transform: rotate(360deg); } }
        .cl-spin--green { color: var(--cl-green); }

        /* Chat item */
        .cl-chat-item {
          display: flex; align-items: center; gap: 10px; padding: 12px 14px;
          cursor: pointer; border-bottom: 0.5px solid rgba(0,0,0,0.04);
          transition: background 0.12s; position: relative;
          border-radius: var(--cl-radius);
          width: 100%;
          overflow: hidden; /* prevent long lastMessage causing horizontal scroll */
        }
        .cl-chat-item:hover { background: var(--cl-hover); }
        .cl-chat-item--active { background: var(--cl-active); }
        .cl-chat-item--active::before {
          content: ''; position: absolute; left: 0; top: 0; bottom: 0;
          width: 3px; background: var(--cl-green); border-radius: 0 2px 2px 0;
        }
        .cl-chat-item--checked { background: rgba(37,211,102,0.1); }

        .cl-checkbox { flex-shrink: 0; display: flex; cursor: pointer; }
        .cl-checkbox-on { color: var(--cl-green); }
        .cl-checkbox-off { color: var(--cl-text-3); }

        /* Avatar */
        .cl-avatar-wrap { position: relative; flex-shrink: 0; }
        .cl-avatar {
          width: 42px; height: 42px; border-radius: var(--cl-radius);
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 14px; color: #fff; letter-spacing: 0.5px;
        }
        .cl-window-dot {
          position: absolute; top: -3px; right: -3px; width: 9px; height: 9px;
          border-radius: 50%; background: #f59e0b; border: 2px solid #fff;
        }

        /* Chat info */
        .cl-chat-info { flex: 1; min-width: 0; }
        .cl-chat-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
        .cl-chat-name { font-size: 13.5px; font-weight: 600; color: var(--cl-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }
        .cl-chat-time { font-size: 10.5px; color: var(--cl-text-3); white-space: nowrap; flex-shrink: 0; margin-left: 6px; }
        .cl-chat-mid { display: flex; align-items: center; margin-bottom: 3px; min-width: 0; }
        .cl-chat-preview { font-size: 12px; color: var(--cl-text-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
        .cl-badge {
          min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
          background: var(--cl-green); color: #fff; font-size: 10px; font-weight: 700;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-left: 6px;
        }
        .cl-chat-meta { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; margin-top: 2px; }
        .cl-meta-chip {
          display: inline-flex; align-items: center; gap: 3px;
          font-size: 10px; font-weight: 500; padding: 1px 5px; border-radius: 3px;
        }
        .cl-meta-chip--agent { background: #dbeafe; color: #1d4ed8; }
        .cl-meta-chip--unassigned { color: var(--cl-text-3); font-style: italic; font-weight: 400; }
        .cl-status-pill {
          font-size: 10px; font-weight: 500; padding: 1px 5px; border-radius: 3px;
        }

        /* Pagination */
        .cl-pagination {
          display: flex; align-items: center; justify-content: space-between;
          padding: 8px var(--cl-pad); border-top: 0.5px solid var(--cl-border); background: #fff; flex-shrink: 0;
          position: sticky; bottom: 0;
        }
        .cl-page-info { font-size: 11.5px; color: var(--cl-text-2); }
        .cl-page-total { color: var(--cl-text-3); }
        .cl-page-btns { display: flex; gap: 4px; }
        .cl-page-btn {
          width: 28px; height: 28px; border-radius: var(--cl-radius); border: 0.5px solid var(--cl-border);
          background: #fff; color: var(--cl-text-2); display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s;
        }
        .cl-page-btn:hover:not(:disabled) { background: var(--cl-surface); }
        .cl-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        /* Dropdowns */
        .cl-overlay { position: fixed; inset: 0; z-index: 40; }
        .cl-dropdown {
          position: absolute; top: calc(100% + 4px); z-index: 50;
          background: #fff; border: 0.5px solid var(--cl-border); border-radius: var(--cl-radius);
          box-shadow: 0 4px 16px rgba(0,0,0,0.1); min-width: 220px; max-height: 280px; overflow: hidden;
        }
        .cl-dropdown--right { right: 0; }
        .cl-dropdown-header { padding: 8px 12px; font-size: 11.5px; font-weight: 600; color: var(--cl-text-2); border-bottom: 0.5px solid var(--cl-border); background: var(--cl-surface); }
        .cl-dropdown-loader { padding: 16px; display: flex; justify-content: center; }
        .cl-dropdown-empty { padding: 16px; text-align: center; font-size: 12px; color: var(--cl-text-3); }
        .cl-dropdown-list { max-height: 200px; overflow-y: auto; }
        .cl-agent-row {
          width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 12px;
          background: none; border: none; cursor: pointer; text-align: left; transition: background 0.12s;
        }
        .cl-agent-row:hover:not(:disabled) { background: var(--cl-hover); }
        .cl-agent-row:disabled { opacity: 0.5; }
        .cl-agent-avatar {
          width: 28px; height: 28px; border-radius: var(--cl-radius); flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff;
        }
        .cl-agent-info { flex: 1; min-width: 0; }
        .cl-agent-name { display: block; font-size: 12px; font-weight: 600; color: var(--cl-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cl-agent-email { display: block; font-size: 10.5px; color: var(--cl-text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cl-agent-check { color: var(--cl-green); flex-shrink: 0; }

        /* Filter modal */
        .cl-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 100; }
        .cl-modal {
          position: fixed; left: 50%; top: 50%; transform: translate(-50%,-50%); z-index: 101;
          width: min(420px, calc(100vw - 24px)); max-height: 85vh; display: flex; flex-direction: column;
          background: #fff; border-radius: var(--cl-radius); border: 0.5px solid var(--cl-border);
          box-shadow: 0 8px 32px rgba(0,0,0,0.14);
        }
        .cl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 0.5px solid var(--cl-border); flex-shrink: 0; }
        .cl-modal-title { font-size: 14px; font-weight: 600; color: var(--cl-text); }
        .cl-modal-close { background: none; border: none; cursor: pointer; color: var(--cl-text-3); display: flex; padding: 4px; border-radius: var(--cl-radius); }
        .cl-modal-close:hover { background: var(--cl-surface); }
        .cl-modal-body { flex: 1; overflow-y: auto; padding: 14px 16px; display: flex; flex-direction: column; gap: 14px; }
        .cl-modal-footer { display: flex; gap: 8px; padding: 12px 16px; border-top: 0.5px solid var(--cl-border); flex-shrink: 0; }
        .cl-modal-btn { flex: 1; padding: 8px; border-radius: var(--cl-radius); font-size: 13px; font-weight: 600; cursor: pointer; border: 0.5px solid var(--cl-border); transition: all 0.15s; }
        .cl-modal-btn--ghost { background: #fff; color: var(--cl-text-2); }
        .cl-modal-btn--ghost:hover { background: var(--cl-surface); }
        .cl-modal-btn--primary { background: var(--cl-green); color: #fff; border-color: var(--cl-green); }
        .cl-modal-btn--primary:hover { background: var(--cl-green-dk); border-color: var(--cl-green-dk); }

        .cl-filter-section { display: flex; flex-direction: column; gap: 6px; }
        .cl-filter-label { font-size: 11px; font-weight: 600; color: var(--cl-text-3); text-transform: uppercase; letter-spacing: 0.5px; }
        .cl-filter-list { border: 0.5px solid var(--cl-border); border-radius: var(--cl-radius); overflow: hidden; max-height: 150px; overflow-y: auto; }
        .cl-filter-row {
          width: 100%; display: flex; align-items: center; gap: 8px; padding: 7px 10px;
          background: none; border: none; cursor: pointer; text-align: left; transition: background 0.12s;
          border-bottom: 0.5px solid rgba(0,0,0,0.04);
        }
        .cl-filter-row:last-child { border-bottom: none; }
        .cl-filter-row:hover { background: var(--cl-hover); }
        .cl-filter-row-label { font-size: 12.5px; color: var(--cl-text); flex: 1; }
        .cl-status-dot-sm { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .cl-filter-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .cl-filter-date { display: flex; flex-direction: column; gap: 4px; }
        .cl-date-input {
          width: 100%; padding: 7px 8px; font-size: 11.5px; border: 0.5px solid var(--cl-border);
          border-radius: var(--cl-radius); outline: none; color: var(--cl-text);
        }
        .cl-date-input:focus { border-color: var(--cl-green); }

        @media (max-width: 767px) {
          .cl-root { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default ChatList;